<template>
  <section class="card">
    <p v-if="mode !== 'album'" class="tip">
      与「首页轮播」「首页入口」同级。建表或补列见
      <code>sql/navicat_executable_cms_home_hot_album.sql</code>、
      <code>sql/alter_cms_home_hot_activity_add_sort_order.sql</code>、
      <code>sql/navicat_executable_cms_activity.sql</code>（小程序活动列表/详情主数据）。
    </p>
    <p v-if="errorMsg" class="error">{{ errorMsg }}</p>
    <p v-if="successMsg" class="success">{{ successMsg }}</p>

    <div v-if="showHot" class="section-block">
      <h2 class="section-title">热门活动</h2>
      <p class="section-desc">
        小程序首页<strong>只展示一条</strong>：系统会按活动热度与发布时间自动推荐，无需手工配置推荐权重。
        当前已切为<strong>活动单表</strong>，这里维护的就是活动本身数据。
      </p>
      <div class="panel">
        <div class="head">
          <span class="panel-hint">列表</span>
          <button type="button" class="btn btn-primary" @click="goNewHotActivity">新增活动</button>
        </div>
        <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>排序</th>
              <th>标题</th>
              <th>活动时间</th>
              <th>状态</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in hotActivities" :key="row.id">
              <td>{{ row.sortOrder }}</td>
              <td>{{ row.title }}</td>
              <td class="ellipsis">{{ row.activityTime || '—' }}</td>
              <td>{{ row.statusCode === 'ENABLED' ? '启用' : '停用' }}</td>
              <td>
                <button type="button" class="btn btn-edit" @click.stop="openHotRegs(row)">报名记录</button>
                <button type="button" class="btn btn-edit" @click.stop="goHotActivityDetailEdit(row)">编辑</button>
                <button type="button" class="btn btn-delete" @click="removeHot(row)">删除</button>
              </td>
            </tr>
          </tbody>
        </table>
        </div>
        <p v-if="!hotActivities.length" class="muted">暂无数据，请点击「新增活动」。</p>
      </div>

      <div v-if="showHotRegs" class="mask" @click.self="showHotRegs = false">
        <div class="dialog dialog--shell">
          <div class="dialog-head">
            <h3 class="dialog-title">报名记录（{{ hotRegsTitle }}）</h3>
            <button type="button" class="dialog-close" aria-label="关闭" @click="showHotRegs = false">×</button>
          </div>
          <div class="dialog-body">
            <div class="reg-filters">
              <input v-model.trim="hotRegFilterName" placeholder="按姓名筛选">
              <input v-model.trim="hotRegFilterMobile" placeholder="按手机号筛选">
            </div>
            <table>
              <thead>
                <tr><th>ID</th><th>会员ID</th><th>姓名</th><th>手机号</th><th>单位</th><th>状态</th><th>报名时间</th></tr>
              </thead>
              <tbody>
                <tr v-for="r in filteredHotRegs" :key="r.id">
                  <td>{{ r.id }}</td><td>{{ r.memberId }}</td><td>{{ r.nickname || '—' }}</td><td>{{ r.mobile || '—' }}</td>
                  <td>{{ r.companyName || '—' }}</td><td>{{ r.statusCode || '—' }}</td><td>{{ r.createdAt || '—' }}</td>
                </tr>
              </tbody>
            </table>
            <p v-if="!filteredHotRegs.length" class="muted">无匹配记录。</p>
          </div>
          <div class="dialog-foot">
            <button type="button" class="btn btn-cancel" @click="showHotRegs = false">关闭</button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showAlbum" class="section-block section-block--album album-ref">
      <div class="album-ref-header">
        <h2 class="album-ref-title">
          相册
          <span class="album-ref-count">（共{{ albums.length }}册）</span>
        </h2>
        <div class="album-ref-header-actions">
          <button type="button" class="btn btn-primary" @click="openAlbumDialog(null)">创建相册</button>
          <button type="button" class="btn btn-primary btn-outline" @click="openGroupCreateModal">新建分组</button>
        </div>
      </div>

      <div class="album-ref-toolbar">
        <button type="button" class="album-ref-link" @click="groupManageOpen = true">分组管理</button>
        <div class="album-ref-tabs">
          <label
            v-for="tab in albumCategoryTabs"
            :key="tab"
            class="album-ref-tab"
            :class="{ 'album-ref-tab--on': selectedAlbumTab === tab }"
          >
            <input
              v-model="selectedAlbumTab"
              class="album-ref-tab-input"
              type="radio"
              name="albumCategory"
              :value="tab"
            >
            <span>{{ tab }}</span>
          </label>
        </div>
        <div class="album-ref-toolbar-actions">
          <button
            type="button"
            class="btn btn-delete"
            :disabled="!selectedAlbumIds.length"
            @click="batchDeleteAlbums"
          >删除相册</button>
          <button
            type="button"
            class="btn btn-edit"
            :disabled="!canManageCurrentGroup"
            @click="openGroupDeleteModal"
          >删除分组</button>
          <button
            type="button"
            class="btn btn-edit"
            :disabled="!canManageCurrentGroup"
            @click="openGroupEditModal"
          >编辑分组</button>
        </div>
      </div>

      <p v-if="!filteredAlbums.length" class="album-ref-empty">当前分类暂无相册，点击「创建相册」。</p>
      <div v-else class="album-ref-grid">
        <div v-for="row in filteredAlbums" :key="row.id" class="album-ref-card">
          <label class="album-ref-check">
            <input v-model="selectedAlbumIds" type="checkbox" :value="Number(row.id)">
          </label>
          <div class="album-ref-cover" @click="goAlbumDetail(row)">
            <img class="album-ref-cover-img" :src="toFullUrl(row.coverUrl)" alt="">
            <span v-if="row.homePinned" class="album-ref-pin-badge">置顶</span>
            <span class="album-ref-photo-count">{{ row.photoCount || 0 }}</span>
            <button
              type="button"
              class="album-ref-pin-btn"
              :class="{ 'album-ref-pin-btn--on': row.homePinned }"
              :title="row.homePinned ? '取消置顶' : '置顶'"
              @click.stop="toggleAlbumPin(row)"
            >
              <img class="album-ref-pin-btn-ico" :src="albumCoverPinArrow" alt="">
            </button>
          </div>
          <div class="album-ref-card-body">
            <div class="album-ref-card-title-row">
              <span class="album-ref-card-title" :title="row.title">{{ row.title || '（未填写标题）' }}</span>
            </div>
            <div class="album-ref-card-meta">
              <div class="album-ref-card-meta-row">
                <span>大小：{{ row.fileSizeLabel || '—' }}</span>
                <span>上传人：{{ row.uploaderName || '—' }}</span>
              </div>
              <div class="album-ref-card-meta-row">
                <span>发布时间：{{ row.publishAt || '—' }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="groupManageOpen" class="mask" @click.self="groupManageOpen = false">
      <div class="dialog dialog--shell dialog--groups">
        <div class="dialog-head">
          <h3 class="dialog-title">分组管理</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="groupManageOpen = false">×</button>
        </div>
        <div class="dialog-body">
          <p class="subtip">拖拽左侧把手可调整分组顺序；删除分组将同时删除其下全部相册。</p>
          <div
            v-for="(tab, idx) in albumTabRows"
            :key="tab.id"
            class="tab-item"
            draggable="true"
            @dragstart="onAlbumTabDragStart(idx)"
            @dragover.prevent
            @drop="onAlbumTabDrop(idx)"
            @dragend="onAlbumTabDragEnd"
          >
            <span class="drag-handle" title="拖拽排序">⋮⋮</span>
            <span class="tab-label">{{ tab.tabLabel }}</span>
            <button type="button" class="btn btn-delete btn-sm" @click="confirmDeleteGroupTab(tab)">删</button>
          </div>
          <p v-if="!albumTabRows.length" class="muted">暂无分组，请点击「新建分组」。</p>
        </div>
        <div class="dialog-foot">
          <button type="button" class="btn btn-primary" @click="openGroupCreateModal">新建分组</button>
        </div>
      </div>
    </div>

    <div v-if="albumDialog" class="mask" @click.self="albumDialog = false">
      <div class="dialog dialog--shell">
        <div class="dialog-head">
          <h3 class="dialog-title">创建相册</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="albumDialog = false">×</button>
        </div>
        <div class="dialog-body">
<div class="form-grid">
          <label class="field"><span>标题</span><input v-model="albumForm.title"></label>
          <label class="field">
            <span>所属分组</span>
            <FancySelect
              v-model="albumDialogTabLabel"
              icon="▾"
              :options="albumTabSelectOptions"
              placeholder="选择分组"
            />
          </label>
          <div class="field full">
            <span>封面（必填；更多图片可在详情页上传）</span>
            <div class="upload-row">
              <input ref="albumCoverInput" class="visually-hidden-file" type="file" accept="image/*" @change="onAlbumCover">
              <button type="button" class="btn btn-edit" @click="pickAlbumCover">上传封面</button>
            </div>
            <img v-if="albumForm.coverUrl" class="cover-preview" :src="toFullUrl(albumForm.coverUrl)" alt="">
          </div>
        </div>
        </div>
        <div class="dialog-foot">
          <button type="button" class="btn btn-cancel" @click="albumDialog = false">取消</button>
          <button type="button" class="btn btn-primary" :disabled="albumSaving" @click="submitAlbum">{{ albumSaving ? '创建中…' : '创建并进入详情' }}</button>
        </div>
      </div>
    </div>

    <div v-if="groupCreateOpen" class="mask" @click.self="groupCreateOpen = false">
      <div class="dialog dialog--shell dialog--sm">
        <div class="dialog-head">
          <h3 class="dialog-title">新建分组</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="groupCreateOpen = false">×</button>
        </div>
        <div class="dialog-body">
<label class="field"><span>分组名称</span><input v-model="groupFormName" placeholder="请输入分组名称"></label>
        </div>

        <div class="dialog-foot">
          <button type="button" class="btn btn-cancel" @click="groupCreateOpen = false">取消</button>
          <button type="button" class="btn btn-primary" :disabled="groupSaving" @click="submitGroupCreate">{{ groupSaving ? '保存中…' : '确定' }}</button>
        </div>
      </div>
    </div>

    <div v-if="groupEditOpen" class="mask" @click.self="groupEditOpen = false">
      <div class="dialog dialog--shell dialog--sm">
        <div class="dialog-head">
          <h3 class="dialog-title">编辑分组</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="groupEditOpen = false">×</button>
        </div>
        <div class="dialog-body">
<label class="field"><span>分组名称</span><input v-model="groupFormName" placeholder="请输入分组名称"></label>
        </div>

        <div class="dialog-foot">
          <button type="button" class="btn btn-cancel" @click="groupEditOpen = false">取消</button>
          <button type="button" class="btn btn-primary" :disabled="groupSaving" @click="submitGroupEdit">{{ groupSaving ? '保存中…' : '确定' }}</button>
        </div>
      </div>
    </div>

    <div v-if="groupDeleteOpen" class="mask" @click.self="groupDeleteOpen = false">
      <div class="dialog dialog--shell dialog--sm">
        <div class="dialog-head">
          <h3 class="dialog-title">删除分组</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="groupDeleteOpen = false">×</button>
        </div>
        <div class="dialog-body">
<p class="subtip">确定删除分组「{{ groupDeleteTab && groupDeleteTab.tabLabel }}」？其下全部相册将一并删除，且不可恢复。</p>
        </div>

        <div class="dialog-foot">
          <button type="button" class="btn btn-cancel" @click="groupDeleteOpen = false">取消</button>
          <button type="button" class="btn btn-delete" :disabled="groupSaving" @click="submitGroupDelete">{{ groupSaving ? '删除中…' : '确定删除' }}</button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { adminConfirm } from '@/utils/adminDialog'
import FancySelect from '@/components/FancySelect.vue'
import albumCoverPinArrow from '@/assets/icons/album-cover-pin-arrow.png'
import {
  createAdminAlbumTab,
  createAdminHomeAlbum,
  deleteAdminAlbumTab,
  deleteAdminHomeAlbum,
  pinAdminHomeAlbum,
  deleteAdminHomeHotActivity,
  getAdminAlbumTabs,
  getAdminHomeHotActivityRegistrations,
  getAdminHomeAlbums,
  getAdminHomeHotActivities,
  renameAdminAlbumTab,
  reorderAdminAlbumTabs,
  toMediaUrl,
  uploadImage,
} from '@/api'

export default {
  name: 'HomeSectionsManageView',
  props: {
    /** hot=仅热门活动；album=仅相册；all=两者 */
    mode: { type: String, default: 'all' },
  },
  components: { FancySelect },
  data() {
    return {
      albumCoverPinArrow,
      errorMsg: '',
      successMsg: '',
      hotActivities: [],
      albums: [],
      selectedAlbumTab: '全部',
      albumTabRows: [],
      draggingAlbumTabIndex: -1,
      albumDialog: false,
      albumDialogTabLabel: '',
      albumForm: {
        title: '',
        coverUrl: '',
        tabLabel: '',
        photoUrls: [],
        sortOrder: 0,
        contentText: '',
        statusCode: 'ENABLED',
        remark: '',
      },
      albumSaving: false,
      selectedAlbumIds: [],
      groupManageOpen: false,
      groupCreateOpen: false,
      groupEditOpen: false,
      groupDeleteOpen: false,
      groupFormName: '',
      groupEditTab: null,
      groupDeleteTab: null,
      groupSaving: false,
      showHotRegs: false,
      hotRegsTitle: '',
      hotRegsRows: [],
      hotRegFilterName: '',
      hotRegFilterMobile: '',
      statusOptions: [
        { value: 'ENABLED', label: '启用' },
        { value: 'DISABLED', label: '停用' },
      ],
    }
  },
  mounted() {
    this.loadAll()
  },
  computed: {
    showHot() {
      return this.mode === 'all' || this.mode === 'hot'
    },
    showAlbum() {
      return this.mode === 'all' || this.mode === 'album'
    },
    albumTabs() {
      return (this.albumTabRows || []).map((x) => String(x.tabLabel || '').trim()).filter(Boolean)
    },
    albumCategoryTabs() {
      return ['全部', ...this.albumTabs]
    },
    albumTabSelectOptions() {
      return this.albumTabs.map((t) => ({ value: t, label: t }))
    },
    canManageCurrentGroup() {
      const tab = String(this.selectedAlbumTab || '').trim()
      return !!tab && tab !== '全部' && this.albumTabs.includes(tab)
    },
    filteredAlbums() {
      const tab = String(this.selectedAlbumTab || '全部').trim() || '全部'
      let list = Array.isArray(this.albums) ? [...this.albums] : []
      if (tab !== '全部') {
        list = list.filter((x) => String(x?.tabLabel || '').trim() === tab)
      }
      return list.sort((a, b) => {
        const pa = a.homePinned ? 0 : 1
        const pb = b.homePinned ? 0 : 1
        if (pa !== pb) return pa - pb
        return (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0)
      })
    },
    filteredHotRegs() {
      const name = String(this.hotRegFilterName || '').toLowerCase()
      const mobile = String(this.hotRegFilterMobile || '').toLowerCase()
      return (this.hotRegsRows || []).filter((r) => {
        const n = String(r.nickname || '').toLowerCase()
        const m = String(r.mobile || '').toLowerCase()
        return (!name || n.includes(name)) && (!mobile || m.includes(mobile))
      })
    },
  },
  methods: {
    async loadAlbumTabs() {
      const list = await getAdminAlbumTabs()
      this.albumTabRows = Array.isArray(list)
        ? list.map((x) => ({
            id: x.id,
            tabLabel: String(x.tabLabel || x.tab_label || '').trim(),
            sortOrder: Number(x.sortOrder != null ? x.sortOrder : x.sort_order) || 0,
          })).filter((x) => x.tabLabel)
        : []
    },
    onAlbumTabDragStart(idx) {
      this.draggingAlbumTabIndex = idx
    },
    async onAlbumTabDrop(dropIdx) {
      const from = this.draggingAlbumTabIndex
      this.draggingAlbumTabIndex = -1
      if (from < 0 || from === dropIdx) return
      const arr = [...(this.albumTabRows || [])]
      const moved = arr.splice(from, 1)[0]
      arr.splice(dropIdx, 0, moved)
      this.albumTabRows = arr
      try {
        await reorderAdminAlbumTabs(arr.map((x) => x.tabLabel))
        this.successMsg = '分组顺序已保存'
      } catch (e) {
        this.errorMsg = e.message || '保存分组顺序失败'
        await this.loadAlbumTabs()
      }
    },
    onAlbumTabDragEnd() {
      this.draggingAlbumTabIndex = -1
    },
    emptyAlbum() {
      return {
        title: '',
        coverUrl: '',
        tabLabel: '',
        photoUrls: [],
        sortOrder: 0,
        contentText: '',
        statusCode: 'ENABLED',
        remark: '',
      }
    },
    toFullUrl(url) {
      const u = (url || '').trim()
      if (!u) return ''
      if (u.startsWith('data:')) return u
      return toMediaUrl(u)
    },
    normHotRow(raw) {
      return {
        id: raw.id,
        title: raw.title || '',
        subTitle: raw.subTitle || raw.sub_title || '',
        imageUrl: raw.imageUrl || raw.image_url || '',
        linkTarget: raw.linkTarget || raw.link_target || '',
        listStateLabel: raw.listStateLabel || raw.list_state_label || '进行中',
        activityTime: raw.activityTime || raw.activity_time || '',
        sortOrder: raw.sortOrder != null ? raw.sortOrder : raw.sort_order != null ? raw.sort_order : 0,
        statusCode: raw.statusCode || raw.status_code || 'ENABLED',
      }
    },
    normalizeAlbumPhotoUrls(raw) {
      const list = Array.isArray(raw?.photoUrls) ? [...raw.photoUrls] : Array.isArray(raw?.photo_urls) ? [...raw.photo_urls] : []
      return list.map((u) => String(u || '').trim()).filter(Boolean)
    },
    normAlbumRow(raw) {
      const photoUrls = this.normalizeAlbumPhotoUrls(raw)
      const cover = String(raw?.coverUrl || raw?.cover_url || '').trim()
      return {
        id: raw.id,
        title: raw.title || '',
        coverUrl: cover,
        photoCount: raw.photoCount != null ? raw.photoCount : raw.photo_count != null ? raw.photo_count : photoUrls.length,
        tabLabel: raw.tabLabel || raw.tab_label || '',
        photoUrls,
        sortOrder: raw.sortOrder != null ? raw.sortOrder : raw.sort_order != null ? raw.sort_order : 0,
        statusCode: raw.statusCode || raw.status_code || 'ENABLED',
        remark: raw.remark || '',
        contentText: raw.contentText || raw.content_text || '',
        viewCount: raw.viewCount != null ? raw.viewCount : raw.view_count != null ? raw.view_count : 0,
        likeCount: raw.likeCount != null ? raw.likeCount : raw.like_count != null ? raw.like_count : 0,
        homePinned: !!(raw.homePinned || raw.home_pinned),
        publishAt: raw.publishAt || raw.publish_at || '',
        uploaderName: raw.uploaderName || raw.uploader_name || '',
        fileSizeLabel: raw.fileSizeLabel || raw.file_size_label || '',
        totalSizeBytes: raw.totalSizeBytes != null ? raw.totalSizeBytes : raw.total_size_bytes,
      }
    },
    async loadAll() {
      this.errorMsg = ''
      this.successMsg = ''
      try {
        if (this.showHot) {
          const hotList = await getAdminHomeHotActivities()
          this.hotActivities = Array.isArray(hotList) ? hotList.map((x) => this.normHotRow(x)) : []
        } else {
          this.hotActivities = []
        }
        if (this.showAlbum) {
          await this.loadAlbumTabs()
          const list = await getAdminHomeAlbums()
          this.albums = Array.isArray(list) ? list.map((x) => this.normAlbumRow(x)) : []
          const cats = ['全部', ...this.albumTabs]
          if (!cats.includes(this.selectedAlbumTab)) {
            this.selectedAlbumTab = '全部'
          }
          this.selectedAlbumIds = []
        } else {
          this.albums = []
        }
      } catch (e) {
        this.errorMsg = e.message || '加载失败（请确认后端接口与数据库表已就绪）'
      }
    },
    goNewHotActivity() {
      this.$router.push({
        path: '/content/activities/edit/new',
        query: { fromHot: '1' },
      })
    },
    async goHotActivityDetailEdit(row) {
      if (!row || row.id == null) return
      this.$router.push({
        path: `/content/activities/edit/${Number(row.id)}`,
        query: {
          fromHot: '1',
          hotTitle: String(row && row.title ? row.title : '').trim(),
        },
      })
    },
    async removeHot(row) {
      if (!row || row.id == null) return
      if (!await adminConfirm(`确定删除热门活动「${row.title}」？`)) return
      try {
        await deleteAdminHomeHotActivity(row.id)
        await this.loadAll()
        this.successMsg = '已删除'
      } catch (e) {
        this.errorMsg = e.message || '删除失败'
      }
    },
    async openHotRegs(row) {
      this.showHotRegs = true
      this.hotRegsTitle = row?.title || ''
      this.hotRegsRows = []
      this.hotRegFilterName = ''
      this.hotRegFilterMobile = ''
      try {
        const list = await getAdminHomeHotActivityRegistrations(row.id)
        this.hotRegsRows = Array.isArray(list) ? list : []
      } catch (e) {
        this.errorMsg = e.message || '读取报名记录失败'
      }
    },
    openGroupCreateModal() {
      this.groupFormName = ''
      this.groupCreateOpen = true
    },
    async submitGroupCreate() {
      const name = String(this.groupFormName || '').trim()
      if (!name) {
        this.errorMsg = '分组名称不能为空'
        return
      }
      this.groupSaving = true
      try {
        await createAdminAlbumTab(name)
        this.groupCreateOpen = false
        await this.loadAll()
        this.selectedAlbumTab = name
        this.successMsg = '分组已创建'
      } catch (e) {
        this.errorMsg = e.message || '创建分组失败'
      } finally {
        this.groupSaving = false
      }
    },
    openGroupEditModal() {
      const name = String(this.selectedAlbumTab || '').trim()
      if (!name || name === '全部') return
      const tab = (this.albumTabRows || []).find((x) => x.tabLabel === name)
      if (!tab) return
      this.groupEditTab = tab
      this.groupFormName = tab.tabLabel
      this.groupEditOpen = true
    },
    async submitGroupEdit() {
      const tab = this.groupEditTab
      const name = String(this.groupFormName || '').trim()
      if (!tab || !name) {
        this.errorMsg = '分组名称不能为空'
        return
      }
      this.groupSaving = true
      try {
        await renameAdminAlbumTab(tab.id, name)
        this.groupEditOpen = false
        if (this.selectedAlbumTab === tab.tabLabel) this.selectedAlbumTab = name
        await this.loadAll()
        this.successMsg = '分组已更新'
      } catch (e) {
        this.errorMsg = e.message || '更新分组失败'
      } finally {
        this.groupSaving = false
      }
    },
    openGroupDeleteModal() {
      const name = String(this.selectedAlbumTab || '').trim()
      if (!name || name === '全部') return
      const tab = (this.albumTabRows || []).find((x) => x.tabLabel === name)
      if (!tab) return
      this.groupDeleteTab = tab
      this.groupDeleteOpen = true
    },
    confirmDeleteGroupTab(tab) {
      this.groupDeleteTab = tab
      this.groupDeleteOpen = true
    },
    async submitGroupDelete() {
      const tab = this.groupDeleteTab
      if (!tab) return
      this.groupSaving = true
      try {
        await deleteAdminAlbumTab(tab.id)
        this.groupDeleteOpen = false
        if (this.selectedAlbumTab === tab.tabLabel) this.selectedAlbumTab = '全部'
        await this.loadAll()
        this.successMsg = '分组及其相册已删除'
      } catch (e) {
        this.errorMsg = e.message || '删除分组失败'
      } finally {
        this.groupSaving = false
      }
    },
    goAlbumDetail(row) {
      if (!row || row.id == null) return
      this.$router.push(`/cms/home/albums/${Number(row.id)}`)
    },
    async batchDeleteAlbums() {
      const ids = (this.selectedAlbumIds || []).map((x) => Number(x)).filter((x) => x > 0)
      if (!ids.length) return
      if (!await adminConfirm(`确定删除选中的 ${ids.length} 个相册？`)) return
      try {
        for (const id of ids) {
          await deleteAdminHomeAlbum(id)
        }
        this.selectedAlbumIds = []
        await this.loadAll()
        this.successMsg = '已删除选中相册'
      } catch (e) {
        this.errorMsg = e.message || '批量删除失败'
      }
    },
    openAlbumDialog() {
      this.errorMsg = ''
      if (!this.albumTabs.length) {
        this.errorMsg = '请先新建分组'
        return
      }
      const nextSort = (this.albums || []).reduce((m, x) => Math.max(m, Number(x.sortOrder || 0)), 0) + 1
      const cur = String(this.selectedAlbumTab || '').trim()
      const tab = String((cur && cur !== '全部' ? cur : this.albumTabs[0]) || '').trim()
      this.albumDialogTabLabel = tab
      this.albumForm = { ...this.emptyAlbum(), sortOrder: nextSort, tabLabel: tab }
      this.albumDialog = true
    },
    pickAlbumCover() {
      const el = this.$refs.albumCoverInput
      if (el) el.click()
    },
    async onAlbumCover(e) {
      const file = e?.target?.files?.[0]
      if (!file) return
      try {
        const data = await uploadImage(file)
        const url = data?.url ? String(data.url) : ''
        if (url) this.albumForm.coverUrl = url
      } catch (err) {
        this.errorMsg = err.message || '封面上传失败'
      } finally {
        if (e?.target) e.target.value = ''
      }
    },
    async submitAlbum() {
      if (!String(this.albumForm.title || '').trim()) {
        this.errorMsg = '标题必填'
        return
      }
      if (!String(this.albumDialogTabLabel || '').trim()) {
        this.errorMsg = '请选择所属分组'
        return
      }
      if (!String(this.albumForm.coverUrl || '').trim()) {
        this.errorMsg = '请上传封面'
        return
      }
      this.albumSaving = true
      this.errorMsg = ''
      try {
        const payload = {
          title: this.albumForm.title.trim(),
          coverUrl: this.albumForm.coverUrl,
          tabLabel: String(this.albumDialogTabLabel || '').trim() || null,
          photoUrls: [],
          sortOrder: Number(this.albumForm.sortOrder) || 0,
          statusCode: 'ENABLED',
          remark: null,
          contentText: null,
          viewCount: 0,
          likeCount: 0,
          homePinned: false,
        }
        const created = await createAdminHomeAlbum(payload)
        this.albumDialog = false
        const id = created && created.id != null ? Number(created.id) : 0
        if (id > 0) {
          this.$router.push(`/cms/home/albums/${id}`)
        } else {
          await this.loadAll()
          this.successMsg = '相册已创建'
        }
      } catch (e) {
        this.errorMsg = e.message || '创建失败'
      } finally {
        this.albumSaving = false
      }
    },
    async removeAlbum(row) {
      if (!row || row.id == null) return
      if (!await adminConfirm(`删除相册「${row.title}」？`)) return
      try {
        await deleteAdminHomeAlbum(row.id)
        await this.loadAll()
        this.successMsg = '已删除'
      } catch (e) {
        this.errorMsg = e.message || '删除失败'
      }
    },
    async toggleAlbumPin(row) {
      if (!row || row.id == null) return
      const next = !row.homePinned
      try {
        await pinAdminHomeAlbum(row.id, next)
        await this.loadAll()
        this.successMsg = next ? '已置顶（同分类下其他相册已自动取消置顶）' : '已取消置顶'
      } catch (e) {
        this.errorMsg = e.message || '置顶操作失败'
      }
    },
  },
}
</script>

<style scoped>
.card { background: #fff; border-radius: 12px; padding: 18px; max-width: 100%; box-sizing: border-box; }
.tip { background: #f5f8ff; border: 1px solid #dae5ff; color: #2a4b8d; padding: 10px 12px; border-radius: 8px; margin-bottom: 12px; line-height: 1.55; font-size: 13px; }
.section-block { margin-bottom: 28px; min-width: 0; }
.section-block--album { margin-bottom: 0; }
.section-title {
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 800;
  color: #101828;
  border-left: 4px solid #2f6af6;
  padding-left: 10px;
}
.section-desc {
  margin: 0 0 12px;
  font-size: 13px;
  color: #475467;
  line-height: 1.55;
}
/* —— 相册管理（参考图：顶部分类 + 卡片网格） —— */
.album-ref { padding-top: 4px; }
.album-ref-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}
.album-ref-title {
  margin: 0;
  font-size: 20px;
  font-weight: 800;
  color: #101828;
  border-left: none;
  padding-left: 0;
}
.album-ref-count { font-size: 16px; font-weight: 600; color: #667085; }
.album-ref-header-actions { display: flex; gap: 8px; flex-wrap: wrap; }
.btn-outline { background: #fff; color: #2f6af6; border: 1px solid #9bb7ff; }
.btn-xs { padding: 4px 8px; font-size: 11px; }
.album-ref-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 10px 0 14px;
  border-bottom: 1px solid #eef2f7;
  margin-bottom: 16px;
}
.album-ref-link {
  border: none;
  background: none;
  color: #2f6af6;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
}
.album-ref-tabs {
  display: flex;
  align-items: center;
  gap: 14px;
  flex: 1;
  flex-wrap: wrap;
  min-width: 0;
}
.album-ref-tab {
  display: inline-flex;
  align-items: center;
  font-size: 13px;
  color: #667085;
  cursor: pointer;
  user-select: none;
}
.album-ref-tab-input { position: absolute; opacity: 0; pointer-events: none; }
.album-ref-tab--on { color: #2f6af6; font-weight: 700; }
.album-ref-toolbar-actions { display: flex; gap: 8px; flex-wrap: wrap; margin-left: auto; }
.album-ref-empty { color: #98a2b3; font-size: 13px; padding: 24px 0; text-align: center; }
.album-ref-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
}
.album-ref-card {
  position: relative;
  border: 1px solid #eef2f7;
  border-radius: 10px;
  background: #fff;
  overflow: hidden;
  transition: box-shadow .15s;
}
.album-ref-card:hover { box-shadow: 0 6px 18px rgba(16, 24, 40, 0.08); }
.album-ref-check {
  position: absolute;
  left: 8px;
  top: 8px;
  z-index: 3;
  width: 18px;
  height: 18px;
}
.album-ref-check input { width: 16px; height: 16px; cursor: pointer; }
.album-ref-cover {
  position: relative;
  aspect-ratio: 4 / 3;
  background: #eef2f6;
  cursor: pointer;
}
.album-ref-cover-img { width: 100%; height: 100%; object-fit: cover; display: block; }
.album-ref-pin-badge {
  position: absolute;
  left: 8px;
  bottom: 8px;
  z-index: 2;
  background: linear-gradient(135deg, #ff9a3c, #ff6b00);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 4px;
}
.album-ref-photo-count {
  position: absolute;
  right: 8px;
  bottom: 8px;
  z-index: 2;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 11px;
  line-height: 20px;
  text-align: center;
}
.album-ref-pin-btn {
  position: absolute;
  right: 8px;
  top: 8px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border-radius: 6px;
  border: none;
  background: #2f6af6;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(47, 106, 246, 0.35);
}
.album-ref-pin-btn-ico {
  width: 20px;
  height: 20px;
  display: block;
  pointer-events: none;
}
.album-ref-pin-btn--on { background: #ff6b00; box-shadow: 0 2px 6px rgba(255, 107, 0, 0.35); }
.album-ref-card-body { padding: 10px 12px 12px; }
.album-ref-card-title-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}
.album-ref-card-title {
  flex: 1;
  font-size: 13px;
  font-weight: 700;
  color: #101828;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.album-ref-card-meta {
  margin-top: 8px;
  font-size: 11px;
  color: #98a2b3;
  line-height: 1.5;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.album-ref-card-meta-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
}
.dialog--groups { max-width: 520px; }

.album-grid { display: grid; grid-template-columns: minmax(0, 320px) minmax(0, 1fr); gap: 14px; }
.panel { border: 1px solid #eef2f7; border-radius: 12px; padding: 14px; min-width: 0; }
.head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; gap: 10px; flex-wrap: wrap; }
.head-tools { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.tab-input { width: 220px; }
.panel-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 10px; }
.panel-head h3 { margin: 0; font-size: 14px; color: #101828; }
.subtip { color: #98a2b3; font-size: 12px; margin: 0 0 10px; }
.empty { color: #98a2b3; font-size: 13px; padding: 8px 0; }
.tab-item {
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #eef2f7;
  border-radius: 10px;
  padding: 8px;
  margin-top: 8px;
  cursor: pointer;
}
.tab-item.active { border-color: #9bb7ff; background: #f5f8ff; }
.drag-handle {
  color: #98a2b3;
  font-size: 14px;
  line-height: 1;
  user-select: none;
  cursor: grab;
}
.tab-index {
  width: 20px;
  height: 20px;
  border-radius: 8px;
  background: #eef4ff;
  color: #2f6af6;
  font-size: 12px;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.tab-label { flex: 1; min-width: 0; }
.panel-hint { font-size: 13px; color: #667085; font-weight: 600; }
.muted { color: #98a2b3; font-size: 13px; margin: 8px 0 0; }
.item-row {
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid #eef2f7;
  border-radius: 10px;
  padding: 10px 12px;
  margin-top: 8px;
  background: #fbfdff;
}
.item-row-index {
  width: 22px;
  height: 22px;
  border-radius: 8px;
  background: #eef4ff;
  color: #2f6af6;
  font-size: 11px;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.item-row-main { flex: 1; min-width: 0; }
.item-row-title {
  font-size: 13px;
  font-weight: 600;
  color: #101828;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.item-row-meta {
  margin-top: 4px;
  font-size: 12px;
  color: #98a2b3;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.item-row-actions { display: flex; align-items: center; gap: 6px; flex-shrink: 0; flex-wrap: wrap; justify-content: flex-end; }
.pin-tag { color: #2f6af6; font-weight: 700; }
.btn-sm { padding: 6px 10px; font-size: 12px; }
.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; max-width: 100%; }
.field { display: flex; flex-direction: column; gap: 6px; font-size: 13px; color: #344054; min-width: 0; }
.field > span { font-weight: 600; }
.full { grid-column: span 2; }
input { border: 1px solid #d0d7e2; border-radius: 8px; padding: 8px 10px; max-width: 100%; box-sizing: border-box; min-width: 0; }
textarea { border: 1px solid #d0d7e2; border-radius: 8px; padding: 8px 10px; max-width: 100%; box-sizing: border-box; min-width: 0; resize: vertical; }
.hint { color: #98a2b3; font-size: 12px; }
.upload-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.thumb { width: 96px; height: 64px; object-fit: cover; border-radius: 8px; border: 1px solid #e5e7eb; }
.photo-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(88px, 1fr)); gap: 10px; margin-top: 10px; }
.photo-item { position: relative; border: 1px solid #e7e9ee; border-radius: 10px; overflow: hidden; background: #fff; cursor: grab; }
.photo-cover-tag { position: absolute; left: 6px; top: 6px; z-index: 1; background: #2f6af6; color: #fff; font-size: 11px; padding: 2px 6px; border-radius: 4px; }
.photo-thumb { width: 100%; height: 88px; object-fit: cover; display: block; }
.photo-remove {
  position: absolute;
  right: 6px;
  top: 6px;
  width: 22px;
  height: 22px;
  border-radius: 999px;
  border: 1px solid rgba(0,0,0,.12);
  background: rgba(255,255,255,.92);
  color: #111;
  line-height: 18px;
  font-size: 16px;
  cursor: pointer;
}
.btn { border-radius: 8px; padding: 6px 12px; cursor: pointer; border: 1px solid transparent; font-size: 12px; }
.btn:disabled { opacity: 0.45; cursor: not-allowed; }
.btn + .btn { margin-left: 8px; }
.btn-primary { background: #2f6af6; color: #fff; }
.btn-edit { border-color: #bfc9db; background: #fff; color: #26457d; }
.btn-delete { border-color: #ffc8c2; background: #fff1f0; color: #d92d20; }
.btn-cancel { border-color: #d0d7e2; background: #fff; color: #344054; }
.hot-link-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin: 4px 0 6px;
}
.btn-link {
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.error { color: #d92d20; }
.success { color: #027a48; margin-bottom: 8px; }
.table-wrap { width: 100%; overflow-x: auto; -webkit-overflow-scrolling: touch; }
table { width: 100%; border-collapse: collapse; font-size: 13px; min-width: 520px; }
th, td { border-bottom: 1px solid #edf0f5; padding: 8px; text-align: left; word-break: break-word; }
.ellipsis { max-width: 180px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dialog { position: relative; width: 100%; max-width: 640px; max-height: 92vh; overflow-y: auto; background: #fff; border-radius: 12px; padding: 16px; box-sizing: border-box; }
.dialog--sm { max-width: 420px; }
.cover-preview { margin-top: 8px; max-width: 200px; max-height: 140px; object-fit: cover; border-radius: 8px; border: 1px solid #eef2f7; }
.subtip { font-size: 13px; color: #667085; line-height: 1.5; margin: 8px 0; }
.visually-hidden-file {
  position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
  overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; border: 0; opacity: 0;
}
@media (max-width: 1080px) {
  .album-grid { grid-template-columns: 1fr; }
}
</style>
